import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography, Stack, IconButton, Collapse, Divider, Button, CircularProgress } from '@mui/material';
import Iconify from '../../../../components/Iconify';
import RestoreIcon from '../../../../assets/deposit/restore.png';
import Image from '../../../../components/Image';
import Restore from '../../../../assets/withdraw/restore-black.png';
import palette from '../../../../theme/palette';
import { getUserMemberBalance } from '../../../../redux/slices/lookup';
import ResponsiveModal from '../../../../components/ResponsiveModal';
import useLocales from '../../../../hooks/useLocales';
import conObj from '../../../../utils/connection-assistant';

const Details = ({ open, onClose, data }) => {
  const { translate } = useLocales();
  const { memberInfo } = useSelector((x) => ({
    ...x.lookup,
  }));
  const [expandedOthers, setExpandedOthers] = useState(false);
  const dispatch = useDispatch();

  const currency = data?.currency || 'MYR';
  const walletBalance = data?.walletBalance || 0;
  const lockedBalance = data?.lockedBalance || 0;
  const providers = memberInfo?.userGameAccount || [];
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [providerCredits, setProviderCredits] = useState({});
  const [othersBalance, setOthersBalance] = useState(0);
  const [loadingWallet, setLoadingWallet] = useState(false);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [loadingProviderCredits, setLoadingProviderCredits] = useState({});

  const loadingOthersBalance = Object.values(loadingProviderCredits).some((v) => v === true);

  const fetchProviderCredit = async (providerId) => {
    setLoadingProviderCredits((prev) => ({ ...prev, [providerId]: true }));

    try {
      const res = await conObj.get(`v1/get-provider-credit?provider_id=${providerId}`);

      let creditStr = res?.data?.data?.credit ?? '0';
      creditStr = creditStr.replace(/,/g, '');
      const credit = parseFloat(creditStr);

      return Number.isNaN(credit) ? 0 : credit;
    } catch (err) {
      console.error('Error fetching provider credit:', err);
      return 0;
    } finally {
      setLoadingProviderCredits((prev) => ({ ...prev, [providerId]: false }));
    }
  };

  const fetchWalletBalance = async () => {
    try {
      setLoadingWallet(true);
      await dispatch(getUserMemberBalance());
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingWallet(false);
    }
  };

  const fetchAllProviders = async () => {
    if (loadingProviders) return; // prevent multiple calls
    setLoadingProviders(true);

    let total = 0;
    const credits = {};

    await Promise.all(
      providers.map(async (item) => {
        const credit = await fetchProviderCredit(item.provider_id);
        credits[item.provider_id] = credit;
        total += credit;
      })
    );

    setProviderCredits(credits);
    setOthersBalance(total);
    setLoadingProviders(false);
  };

  useEffect(() => {
    if (!open) return;

    // reset old data (important: no caching)
    setProviderCredits({});
    setOthersBalance(0);

    fetchWalletBalance();
    fetchAllProviders();
  }, [open]);

  // useEffect(() => {
  //   if (!open || Object.keys(providerCredits).length > 0) return;

  //   const loadCredits = async () => {
  //     let total = 0;
  //     const credits = {};

  //     const results = await Promise.all(
  //       providers.map(async (item) => {
  //         const credit = await fetchProviderCredit(item.provider_id);
  //         return { provider_id: item.provider_id, credit };
  //       })
  //     );

  //     results.forEach((r) => {
  //       const value = Number(r.credit || 0);
  //       credits[r.provider_id] = value;
  //       total += value;
  //     });

  //     setProviderCredits(credits);
  //     setOthersBalance(total);
  //   };

  //   loadCredits();
  // }, [open, providers]);

  const formatCurrency = (val) => new Intl.NumberFormat('en-MY', { minimumFractionDigits: 2 }).format(val);

  const Content = (
    <Stack spacing={2} sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Wallet Balance */}
      <Box sx={{ bgcolor: '#F9FAFB', p: 2, borderRadius: '8px' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack spacing={0.5}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400 }}>
              {translate('wallet_balance')}
            </Typography>
            {/* <Button
              sx={{
                border: `2px solid ${palette.header.register_btn.border}`,
                background: `${palette.header.register_btn.bg}`,
                display: 'inline-flex',
                alignItems: 'center',
                px: 1,
                py: 0.5, // Added slight padding for better click area
                borderRadius: '10px',
                gap: 0.5,
                transition: 'opacity 0.2s',
                '&:hover': {
                  opacity: 0.8,
                },
              }}
              onClick={() => dispatch(getUserMemberBalance())}
            >
              <Image src={Restore} sx={{ width: 12, height: 12 }} />
              <Typography
                sx={{
                  fontSize: '10px',
                  fontWeight: 500,
                  color: '#000',
                }}
              >
                {translate('restore')}
              </Typography>
            </Button> */}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography fontWeight={700}>
              <Typography fontWeight={700}>
                {loadingWallet ? <CircularProgress size={16} /> : `${currency} ${formatCurrency(walletBalance)}`}
              </Typography>
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                fetchWalletBalance();
                fetchAllProviders();
              }}
            >
              <Image sx={{ color: 'text.secondary' }} src={RestoreIcon} width={12} height={12} />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      {/* Locked Balance */}
      {/* <Box sx={{ bgcolor: '#F9FAFB', p: 2, borderRadius: '8px' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Locked Balance
          </Typography>
          <Typography fontWeight={700} color="text.primary">
            {currency} {formatCurrency(lockedBalance)}
          </Typography>
        </Stack>
      </Box> */}

      {/* Others Expandable Section */}
      <Box sx={{ bgcolor: '#F9FAFB', borderRadius: '8px', overflow: 'hidden' }}>
        <Box
          onClick={() => setExpandedOthers(!expandedOthers)}
          sx={{ p: 2, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400 }}>
            {translate('others')}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography fontWeight={700}>
              {loadingOthersBalance ? (
                <CircularProgress size={16} sx={{ color: '#000' }} />
              ) : (
                `${currency} ${formatCurrency(othersBalance)}`
              )}
            </Typography>
            <Iconify icon={expandedOthers ? 'mdi:chevron-up' : 'mdi:chevron-down'} />
          </Stack>
        </Box>

        <Collapse in={expandedOthers}>
          <Box sx={{ px: 2, pb: 2 }}>
            {/* Inner white container */}
            <Box sx={{ bgcolor: 'white', borderRadius: '8px', px: 2, py: 1 }}>
              <Stack spacing={1.5}>
                {providers.map((item, idx) => (
                  <Stack key={idx} direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.primary" fontWeight={500}>
                      {item.provider_name}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{
                        bgcolor: 'primary.light',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '4px',
                        minWidth: '50px',
                        textAlign: 'center',
                      }}
                    >
                      {loadingProviderCredits[item.provider_id] ? (
                        <CircularProgress size={14} sx={{ color: '#fff' }} />
                      ) : (
                        formatCurrency(providerCredits[item.provider_id] || 0)
                      )}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Box>
        </Collapse>
      </Box>
    </Stack>
  );
  return (
    <ResponsiveModal
      open={open}
      onClose={onClose}
      title={translate('details')}
      paperPropsSx={{
        borderRadius: '16px',
        height: '700px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ minHeight: '400px' }}>{Content}</Box>
    </ResponsiveModal>
  );
};

export default Details;
